import React, { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import LoadingSpinner from './../../../../components/shared/LoadingSpinner.component'
import TableOfContents from './TableOfContents.component'
import Store from './../../store'
import { generateTOC, generateSlug } from './utils/tocGenerator'
import { processContentWithHighlights } from './utils/highlightText'

import './MarkdownViewer.component.scss'

const MarkdownViewer = () => {
  // Generate TOC from content
  const toc = useMemo(() => {
    if (!Store.selectedManual || !Store.selectedManual.content) {
      return []
    }
    return generateTOC(Store.selectedManual.content)
  }, [Store.selectedManual])

  // Process content with search highlighting
  const processedContent = useMemo(() => {
    if (!Store.selectedManual || !Store.selectedManual.content) {
      return ''
    }

    const shouldHighlight = Store.searchMode === 'current' && Store.searchQuery && Store.searchQuery.trim() !== ''

    if (shouldHighlight) {
      return processContentWithHighlights(Store.selectedManual.content, Store.searchQuery)
    }

    return Store.selectedManual.content
  }, [Store.selectedManual, Store.searchQuery, Store.searchMode])

  if (Store.isLoading && !Store.selectedManual) {
    return (
      <div id='MarkdownViewerComponent' className='markdown-viewer'>
        <div className='markdown-viewer__loading'>
          <LoadingSpinner size='md' isActive={true} label='Loading manual...' />
        </div>
      </div>
    )
  }

  if (!Store.selectedManual) {
    return (
      <div id='MarkdownViewerComponent' className='markdown-viewer'>
        <div className='markdown-viewer__empty'>
          <h3>Select a manual from the sidebar</h3>
          <p>Choose a documentation file from the navigation menu to view its contents.</p>
        </div>
      </div>
    )
  }

  return (
    <div id='MarkdownViewerComponent' className='markdown-viewer'>
      <div className='markdown-viewer__header'>
        <h2 className='markdown-viewer__title'>{Store.selectedManual.title}</h2>
      </div>
      <div className='markdown-viewer__content'>
        {/* Table of Contents */}
        <TableOfContents toc={toc} />

        {/* Markdown Content */}
        <div className='markdown-viewer__markdown'>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              // Add IDs to headings for TOC navigation
              h1: ({ node, children, ...props }) => {
                const text = typeof children === 'string' ? children : children?.[0] || ''
                return (
                  <h1 id={generateSlug(text)} {...props}>
                    {children}
                  </h1>
                )
              },
              h2: ({ node, children, ...props }) => {
                const text = typeof children === 'string' ? children : children?.[0] || ''
                return (
                  <h2 id={generateSlug(text)} {...props}>
                    {children}
                  </h2>
                )
              },
              h3: ({ node, children, ...props }) => {
                const text = typeof children === 'string' ? children : children?.[0] || ''
                return (
                  <h3 id={generateSlug(text)} {...props}>
                    {children}
                  </h3>
                )
              },
              h4: ({ node, children, ...props }) => {
                const text = typeof children === 'string' ? children : children?.[0] || ''
                return (
                  <h4 id={generateSlug(text)} {...props}>
                    {children}
                  </h4>
                )
              },
              h5: ({ node, children, ...props }) => {
                const text = typeof children === 'string' ? children : children?.[0] || ''
                return (
                  <h5 id={generateSlug(text)} {...props}>
                    {children}
                  </h5>
                )
              },
              h6: ({ node, children, ...props }) => {
                const text = typeof children === 'string' ? children : children?.[0] || ''
                return (
                  <h6 id={generateSlug(text)} {...props}>
                    {children}
                  </h6>
                )
              },
            }}
          >
            {processedContent}
          </ReactMarkdown>
        </div>
      </div>
      {Store.searchMode === 'current' && Store.searchResults.length > 0 && (
        <div className='markdown-viewer__search-results'>
          <h4>Search Results ({Store.searchResults.length})</h4>
          <ul>
            {Store.searchResults.slice(0, 10).map((result, index) => (
              <li key={index}>
                Line {result.line}: {result.text.substring(0, 100)}
                {result.text.length > 100 ? '...' : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default observer(MarkdownViewer)
